import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import pick from 'lodash/pick';
import Arg from '@spothero/arg';
import SearchAPI from 'api/search';
import {
    ROUTER_HISTORY_CALLED,
    ROUTER_DATA_LOAD_PENDING,
    dataLoadFulfilled,
} from 'router/router-actions';
import SearchUtils from 'search/search-utils';
import ExperimentUtils from 'utils/experiment';
import SearchRequestUtils from 'search-request/search-request-utils';
import {setRouteLoading} from 'search-request/search-request-actions';
import {updateSelected} from 'spot/spot-actions';
import {initialState as searchRequestInitialState} from 'search-request/search-request-reducer';

const createCleanedData = async ({currentCity, data, currentSearchState}) => {
    const {
        page_info: {page_type: pageType, setup},
        rid,
    } = data;
    const searchRequestData = SearchRequestUtils.sanitizeSearchRequestResponse({
        state: searchRequestInitialState,
        response: data,
    });
    const requestData = {
        pageType,
        setup,
        citySlug: setup.city.slug,
        isNewCity: setup.city.slug !== currentCity.data.slug,
    };
    const loadedData = await SearchRequestUtils.loadDataSequence(requestData);
    const cleanedData = {
        searchRequest: searchRequestData,
        checkout: {
            rid,
        },
        ...loadedData,
    };

    if (!cleanedData.city) {
        cleanedData.city = currentCity;
    }

    cleanedData.search = SearchUtils.prepareStoreData({
        initData: cleanedData,
        experimentUtils: ExperimentUtils,
        pageType,
        currentSearchState,
    });

    return cleanedData;
};

let isAtSpotDetails = false;

export const routerMiddleware = history => ({
    dispatch,
    getState,
}) => next => async action => {
    switch (action.type) {
        case ROUTER_HISTORY_CALLED: {
            const {
                payload: {method, url, args},
            } = action;

            history[method](url, ...args);

            break;
        }

        case ROUTER_DATA_LOAD_PENDING: {
            const {
                city,
                search: searchState,
                searchRequest,
                spots: {data: spots},
            } = getState();
            const {
                payload: {
                    historyAction,
                    location: {pathname, search, state: historyState},
                },
            } = action;
            const {pageType} = searchRequest;
            const isSearchPage =
                pageType === 'search' ||
                pageType === 'city' ||
                pageType === 'destination' ||
                pageType === 'event';
            let requestData;

            if (isSearchPage && includes(pathname, '/spot-details/')) {
                // don't run route loading if going to spot-details or a full page loader/white screen will happen
                isAtSpotDetails = true;

                dispatch(
                    updateSelected({
                        spot: find(spots, {
                            spotId: parseInt(
                                pathname.split('spot-details/')[1],
                                10
                            ),
                        }),
                        referrer: get(historyState, 'referrer', 'list'),
                    })
                );

                break;
            } else if (
                isSearchPage &&
                isAtSpotDetails &&
                historyAction === 'POP'
            ) {
                // going back to search from spot details
                isAtSpotDetails = false;

                dispatch(updateSelected());

                break;
            }

            isAtSpotDetails = false;

            dispatch(setRouteLoading());

            if (historyState && historyState.cleanedParams) {
                // clean params endpoint was already hit, just go ahead and update the store with the new data (happens during a search input)
                requestData = historyState.cleanedParams;
            } else {
                // when going back to home, the current monthly and search_string need to be used to reset the search bar properly
                const query =
                    pathname === '/'
                        ? `?${Arg.stringify(
                              pick(searchRequest, ['monthly', 'search_string'])
                          )}`
                        : search;
                // data was not included in the routing call, clean data based on API first
                const {
                    data: {data},
                } = await SearchAPI.cleanParams({url: `${pathname}${query}`});

                requestData = data;
            }

            const cleanedData = await createCleanedData({
                currentCity: city,
                data: requestData,
                currentSearchState: searchState,
            });

            dispatch(dataLoadFulfilled(cleanedData));

            break;
        }
    }

    return next(action);
};
